import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  fetchOrganisationSocialApprovalRequest,
  fetchOrganisationSocialConnections,
  fetchOrganisationSocialDisconnect,
} from '../../services/organisation.service';
import {
  ISocialAccountConnection,
  ISocialConnectionState,
  TSocialPlatform,
} from '../../types/@organisation';

type Props = {
  organisationId: string;
  callbackMessage?: string;
};

const ORDER: TSocialPlatform[] = ['instagram', 'tiktok', 'x'];

const DISPLAY_NAMES: Record<TSocialPlatform, string> = {
  instagram: 'Instagram',
  tiktok: 'TikTok',
  x: 'X',
};

const getErrorMessage = (error: any, fallback: string): string => {
  const responseMessage = error?.response?.data?.message;

  if (Array.isArray(responseMessage)) {
    return responseMessage.join(', ');
  }

  return responseMessage || error?.message || fallback;
};

const normaliseAccountHandle = (value?: string | null): string => {
  return String(value || '')
    .trim()
    .replace(/^@+/, '');
};

const isConnected = (
  account?: ISocialAccountConnection,
): boolean => {
  return account?.status === 'connected';
};

export default function OrganisationSocialsPanel({
  organisationId,
  callbackMessage = '',
}: Props) {
  const [state, setState] =
    useState<ISocialConnectionState | null>(null);
  const [loading, setLoading] = useState(true);
  const [busyPlatform, setBusyPlatform] =
    useState<TSocialPlatform | null>(null);
  const [message, setMessage] = useState(callbackMessage);

  const load = useCallback(async (): Promise<void> => {
    try {
      setLoading(true);

      const response: any =
        await fetchOrganisationSocialConnections(organisationId);

      setState(response?.data || response);
    } catch (error: any) {
      setMessage(
        getErrorMessage(error, 'Unable to load social accounts.'),
      );
    } finally {
      setLoading(false);
    }
  }, [organisationId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (callbackMessage) {
      setMessage(callbackMessage);
    }
  }, [callbackMessage]);

  const accounts = useMemo<
    Partial<Record<TSocialPlatform, ISocialAccountConnection>>
  >(() => {
    const result: Partial<
      Record<TSocialPlatform, ISocialAccountConnection>
    > = {};

    for (const account of state?.platforms || []) {
      if (ORDER.includes(account.platform)) {
        result[account.platform] = account;
      }
    }

    return result;
  }, [state?.platforms]);

  const isAdmin = state?.myRole === 'admin';

  const authorise = async (
    platform: TSocialPlatform,
  ): Promise<void> => {
    try {
      setBusyPlatform(platform);
      setMessage('');

      const response: any =
        await fetchOrganisationSocialApprovalRequest(
          organisationId,
          platform,
        );

      const payload = response?.data || response;

      if (!payload?.approvalUrl) {
        throw new Error('No approval URL was returned.');
      }

      window.location.assign(payload.approvalUrl);
    } catch (error: any) {
      setMessage(
        getErrorMessage(
          error,
          `Unable to connect ${DISPLAY_NAMES[platform]}.`,
        ),
      );
      setBusyPlatform(null);
    }
  };

  const disconnect = async (
    platform: TSocialPlatform,
  ): Promise<void> => {
    const displayName = DISPLAY_NAMES[platform];

    if (
      !window.confirm(
        `Disconnect the organisation's ${displayName} account?`,
      )
    ) {
      return;
    }

    try {
      setBusyPlatform(platform);
      setMessage('');

      await fetchOrganisationSocialDisconnect(
        organisationId,
        platform,
      );

      setMessage(`${displayName} disconnected.`);
      await load();
    } catch (error: any) {
      setMessage(
        getErrorMessage(
          error,
          `Unable to disconnect ${displayName}.`,
        ),
      );
    } finally {
      setBusyPlatform(null);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          py: 6,
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <Box>
        <Typography
          sx={{
            fontSize: 24,
            fontWeight: 700,
            color: '#17324d',
          }}
        >
          Social accounts
        </Typography>

        <Typography sx={{ mt: 1, color: '#5f7285' }}>
          Authorise the club accounts that event administrators may
          use for direct posting.
        </Typography>
      </Box>

      {message && (
        <Alert
          severity={
            message.toLowerCase().includes('success') ||
            message.toLowerCase().includes('connected') ||
            message.toLowerCase().includes('disconnected')
              ? 'success'
              : 'info'
          }
        >
          {message}
        </Alert>
      )}

      {!isAdmin && (
        <Alert severity="info">
          Only organisation administrators can connect, reconnect or
          disconnect social accounts.
        </Alert>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: 'repeat(2,minmax(0,1fr))',
          },
          gap: 2,
        }}
      >
        {ORDER.map((platform) => {
          const account = accounts[platform];
          const displayName =
            account?.displayName || DISPLAY_NAMES[platform];
          const connected = isConnected(account);
          const supported =
            account?.authorisationSupported === true;
          const canConfigure = isAdmin && supported;
          const busy = busyPlatform === platform;
          const handle = normaliseAccountHandle(
            account?.accountName,
          );

          const mediaTypes = [
            account?.imagePublishingSupported ? 'images' : '',
            account?.videoPublishingSupported ? 'video' : '',
          ].filter(Boolean);

          return (
            <Paper
              key={platform}
              elevation={0}
              sx={{
                p: 2.5,
                border: '1px solid #e3e8ef',
                borderRadius: '14px',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  gap: 2,
                }}
              >
                <Box>
                  <Typography
                    sx={{
                      fontSize: 20,
                      fontWeight: 700,
                      color: '#17324d',
                    }}
                  >
                    {displayName}
                  </Typography>

                  <Typography
                    sx={{
                      mt: 0.5,
                      color: '#5f7285',
                    }}
                  >
                    {connected && handle
                      ? `@${handle}`
                      : 'No account connected'}
                  </Typography>
                </Box>

                <Chip
                  size="small"
                  color={
                    connected
                      ? 'success'
                      : supported
                        ? 'default'
                        : 'warning'
                  }
                  label={
                    connected
                      ? 'Connected'
                      : supported
                        ? 'Not connected'
                        : 'Not available'
                  }
                />
              </Box>

              <Box sx={{ mt: 2 }}>
                <Typography sx={{ color: '#5f7285' }}>
                  Direct posting:{' '}
                  {account?.canDirectPublish
                    ? 'Available'
                    : connected
                      ? 'Required permission not granted'
                      : supported
                        ? 'Connect account first'
                        : 'Not currently available'}
                </Typography>

                <Typography
                  sx={{
                    mt: 1,
                    color: '#5f7285',
                  }}
                >
                  Media:{' '}
                  {mediaTypes.length > 0
                    ? mediaTypes.join(' and ')
                    : 'Not configured'}
                </Typography>

                <Typography
                  sx={{
                    mt: 1,
                    color: '#5f7285',
                  }}
                >
                  Insights:{' '}
                  {account?.insightsSupported
                    ? 'Supported'
                    : 'Not currently available'}
                </Typography>
              </Box>

              {account?.lastError && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  {account.lastError}
                </Alert>
              )}

              <Box
                sx={{
                  mt: 2.5,
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1,
                }}
              >
                {connected ? (
                  <>
                    <Button
                      variant="contained"
                      disabled={!canConfigure || busy}
                      onClick={() => {
                        void authorise(platform);
                      }}
                    >
                      {busy ? 'Working�' : 'Reconnect'}
                    </Button>

                    <Button
                      color="error"
                      disabled={!isAdmin || busy}
                      onClick={() => {
                        void disconnect(platform);
                      }}
                    >
                      Disconnect
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="contained"
                    disabled={!canConfigure || busy}
                    onClick={() => {
                      void authorise(platform);
                    }}
                  >
                    {busy
                      ? 'Opening�'
                      : `Connect ${displayName}`}
                  </Button>
                )}
              </Box>

              {!supported && (
                <Typography
                  sx={{
                    mt: 1.5,
                    fontSize: 13,
                    color: '#7c8b99',
                  }}
                >
                  Account authorisation is not yet available for this
                  platform.
                </Typography>
              )}
            </Paper>
          );
        })}
      </Box>
    </Box>
  );
}
